// React-Router-compatible hooks implemented on top of Vike's routing.
//
// The rest of the app imports navigation primitives from `shared/Router`, so
// reimplementing them here on Vike's `usePageContext` + `navigate()` lets us
// drop React Router entirely without touching consumer components.
//
// Note on URLs: Vike's `pageContext.urlPathname` is *base-less* (the `/search`
// prefix is stripped), whereas `navigate()` expects the full browser URL
// including the base. `withBase`/`resolveTo` bridge the two.

import {
  useCallback,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type ReactNode,
} from "react";
import { navigate as vikeNavigate } from "vike/client/router";
import { usePageContext } from "vike-react/usePageContext";

import {
  getNavigationState,
  subscribeNavigation,
  type NavigationState,
} from "./transitionStore";

export { useRouteError } from "./useRouteError";
export {
  setNavigationState,
  getNavigationState,
  subscribeNavigation,
} from "./transitionStore";
export type { NavigationState } from "./transitionStore";

const BASE_URL = import.meta.env.BASE_URL || "/"; // e.g. "/search/"

/** Prepend the Vite base (e.g. `/search`) to a base-less logical path. */
function withBase(logicalPath: string): string {
  const base = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
  const path = logicalPath.startsWith("/") ? logicalPath : `/${logicalPath}`;
  return `${base}${path}` || "/";
}

/** Resolve a (possibly relative) navigation target against the current path. */
function resolveTo(to: string, currentPathname: string): string {
  if (to.startsWith("/")) return to;
  const base = currentPathname.endsWith("/")
    ? currentPathname.slice(0, -1)
    : currentPathname;
  return `${base}/${to}`;
}

export interface NavigateOptions {
  replace?: boolean;
  keepScrollPosition?: boolean;
}

export type NavigateFunction = (
  to: string | number,
  options?: NavigateOptions
) => void;

export function useNavigate(): NavigateFunction {
  const pageContext = usePageContext();
  const currentPathname = pageContext.urlPathname;

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === "number") {
        if (typeof window !== "undefined") window.history.go(to);
        return;
      }
      const logical = resolveTo(to, currentPathname);
      void vikeNavigate(withBase(logical), {
        overwriteLastHistoryEntry: options?.replace,
        keepScrollPosition: options?.keepScrollPosition,
      });
    },
    [currentPathname]
  );
}

export interface Location {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

export function useLocation(): Location {
  const pageContext = usePageContext();
  const { urlPathname } = pageContext;
  const search = pageContext.urlParsed.searchOriginal ?? "";
  // `hashOriginal` is unavailable server-side (HTTP requests omit the hash) and
  // accessing it there triggers a Vike warning, so only read it in the browser.
  const hash =
    typeof window !== "undefined"
      ? pageContext.urlParsed.hashOriginal ?? ""
      : "";
  return useMemo(
    () => ({
      pathname: urlPathname,
      search,
      hash,
      state: null,
      key: "default",
    }),
    [urlPathname, search, hash]
  );
}

export function useParams<
  T extends Record<string, string | undefined> = Record<
    string,
    string | undefined
  >
>(): T {
  const pageContext = usePageContext();
  const slug = pageContext.routeParams?.slug;
  return useMemo(() => ({ propertySlug: slug } as unknown as T), [slug]);
}

export type SetURLSearchParams = (
  next: URLSearchParams | string,
  options?: NavigateOptions
) => void;

function subscribeToLocation(cb: () => void) {
  window.addEventListener("popstate", cb);
  window.addEventListener("__searchchange__", cb);
  return () => {
    window.removeEventListener("popstate", cb);
    window.removeEventListener("__searchchange__", cb);
  };
}

export function useSearchParams(): [URLSearchParams, SetURLSearchParams] {
  const pageContext = usePageContext();
  const ssrSearch = pageContext.urlParsed.searchOriginal ?? "";
  const pathname = pageContext.urlPathname;

  const search = useSyncExternalStore(
    subscribeToLocation,
    () => window.location.search,
    () => ssrSearch
  );

  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const setSearchParams = useCallback<SetURLSearchParams>(
    (next, options) => {
      const qs = typeof next === "string" ? next : next.toString();
      // Use window.location.pathname (always current) rather than the
      // pageContext-derived `pathname`, which can be stale immediately after
      // a client-side navigation (Vike updates pageContext asynchronously).
      const currentPath =
        typeof window !== "undefined"
          ? window.location.pathname
          : withBase(pathname);
      const url = currentPath + (qs ? `?${qs}` : "");
      if (options?.replace ?? true) {
        window.history.replaceState(window.history.state, "", url);
      } else {
        window.history.pushState(window.history.state, "", url);
      }
      window.dispatchEvent(new Event("__searchchange__"));
    },
    [pathname]
  );

  return [searchParams, setSearchParams];
}

export interface PathMatch {
  params: Record<string, string>;
  pathname: string;
  pattern: { path: string };
}

/** Match an RR-style pattern (`/:propertySlug/results`) against a pathname. */
function matchPath(pattern: string, pathname: string): PathMatch | null {
  const keys = (pattern.match(/:[^/]+/g) ?? []).map((k) => k.slice(1));
  const regexSource =
    "^" + pattern.replace(/:[^/]+/g, "([^/]+)").replace(/\//g, "\\/") + "\\/?$";
  const re = new RegExp(regexSource);
  const m = pathname.match(re);
  if (!m) return null;
  const params: Record<string, string> = {};
  keys.forEach((k, i) => {
    params[k] = m[i + 1] ?? "";
  });
  return { params, pathname, pattern: { path: pattern } };
}

export function useMatch(pattern: string): PathMatch | null {
  const pageContext = usePageContext();
  return useMemo(
    () => matchPath(pattern, pageContext.urlPathname),
    [pattern, pageContext.urlPathname]
  );
}

export function useNavigation(): { state: NavigationState } {
  const state = useSyncExternalStore(
    subscribeNavigation,
    getNavigationState,
    () => "idle" as const
  );
  return { state };
}

export function Navigate({
  to,
  replace,
}: {
  to: string;
  replace?: boolean;
}): null {
  const navigate = useNavigate();
  useEffect(() => {
    navigate(to, { replace });
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

/**
 * Layouts now receive their page as `children`, so `<Outlet />` is unused.
 * Kept as a no-op export for backward compatibility.
 */
export function Outlet(): null {
  return null;
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  children?: ReactNode;
}

export function Link({ to, children, onClick, ...rest }: LinkProps) {
  const navigate = useNavigate();
  return (
    <a
      href={withBase(to)}
      onClick={(e) => {
        onClick?.(e);
        if (
          e.defaultPrevented ||
          e.metaKey ||
          e.ctrlKey ||
          e.shiftKey ||
          e.altKey ||
          e.button !== 0
        ) {
          return;
        }
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
